import type { NewSegment } from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadHostQueue } from "../../core/ops/live-ops";
import { lineupItems, type OtherKind, queuedBoxes } from "../../core/primitives/lineup";
import { busyForm, failedForm, failedLoad, idleForm, loaded, loading } from "../../core/primitives/load-status";
import { LineupPickerActions, LineupPickerState, type PickerMode } from "./lineup-picker-model";

export type LineupPickerDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "locationsRepository">;

export interface LineupPickerHost {
  /** Do it: open the stream and/or add the items. Answers the sentence to show, or null when done. */
  commit(items: NewSegment[], locationId: string | null): Promise<string | null>;
}

/** Picks queued boxes and other segments for a stream's line-up, at Go live or mid-stream. */
export class LineupPickerController extends BaseController implements LineupPickerActions {
  readonly state = new LineupPickerState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: LineupPickerDeps,
    readonly host: LineupPickerHost,
  ) {
    super();
    subtree.put(LineupPickerState, this.state);
    subtree.put(LineupPickerActions, this);
    this.sync(() => {
      // A stream goes out from the counter the app is working at.
      const here = deps.storeRepository.locationId;
      const active = deps.locationsRepository.locations.filter((l) => l.isActive && (!here || l.id === here));
      this.state.locations.value = active.map((l) => ({ id: l.id, name: l.name }));
      const chosen = this.state.locationId.value;
      if (!chosen || !active.some((l) => l.id === chosen)) this.state.locationId.value = active[0]?.id ?? null;
    }, [deps.locationsRepository, deps.storeRepository]);
  }

  show = (mode: PickerMode) => {
    this.state.mode.value = mode;
    this.state.picked.value = [];
    this.state.extras.value = [];
    this.state.extraDraft.value = { kind: "UNBOXING", title: "" };
    this.state.submit.value = idleForm;
    this.state.open.value = true;
    void this.#loadBoxes();
  };

  close = () => {
    this.state.open.value = false;
  };

  toggleBox = (breakId: string) => {
    const picked = this.state.picked.value;
    this.state.picked.value = picked.includes(breakId) ? picked.filter((id) => id !== breakId) : [...picked, breakId];
  };

  setExtraKind = (kind: OtherKind) => {
    this.state.extraDraft.update((d) => ({ ...d, kind }));
  };

  setExtraTitle = (title: string) => {
    this.state.extraDraft.update((d) => ({ ...d, title }));
  };

  addExtra = () => {
    const draft = this.state.extraDraft.value;
    const title = draft.title.trim();
    const extras = this.state.extras.value;
    if (!title) return;
    if (!extras.some((e) => e.kind === draft.kind && e.title === title)) {
      this.state.extras.value = [...extras, { kind: draft.kind, title }];
    }
    this.state.extraDraft.value = { kind: draft.kind, title: "" };
  };

  removeExtra = (index: number) => {
    this.state.extras.value = this.state.extras.value.filter((_, i) => i !== index);
  };

  selectLocation = (locationId: string) => {
    this.state.locationId.value = locationId;
  };

  confirm = async () => {
    await this.#commit(lineupItems(this.state.picked.value, this.state.extras.value));
  };

  skip = async () => {
    await this.#commit([]);
  };

  async #commit(items: NewSegment[]): Promise<void> {
    if (this.state.submit.value.busy) return;
    this.state.submit.value = busyForm;
    const failure = await this.host.commit(items, this.state.locationId.value);
    if (this.isDisposed) return;
    if (failure) {
      this.state.submit.value = failedForm(failure);
      return;
    }
    this.state.submit.value = idleForm;
    this.state.open.value = false;
  }

  async #loadBoxes(): Promise<void> {
    const shopId = this.deps.storeRepository.shopId;
    if (!shopId) return;
    this.state.boxesStatus.value = loading;
    const r = await loadHostQueue({ ...this.deps, shopId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.boxesStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.boxes.value = queuedBoxes(r.value);
    this.state.boxesStatus.value = loaded;
  }
}
